import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Phone, MapPin, Star, Clock, Send, Hammer, Navigation } from "lucide-react";
import MapView from "./MapView";
import { api, WS_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/errors";

// Haversine in km
function distanceKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

const statusColor = {
  pending: "bg-[#F59E0B]/15 text-[#B45309]",
  accepted: "bg-[#1C4532]/15 text-[#1C4532]",
  on_the_way: "bg-[#D9734E]/15 text-[#C26240]",
  completed: "bg-[#22C55E]/15 text-[#15803D]",
  rejected: "bg-[#94A3B8]/15 text-[#475569]",
  cancelled: "bg-[#94A3B8]/15 text-[#475569]",
};

export default function BookingDetailsDialog({ bookingId, open, onOpenChange }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [details, setDetails] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  // Load + poll details every 6s while open
  useEffect(() => {
    if (!open || !bookingId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const [d, m] = await Promise.all([
          api.get(`/bookings/${bookingId}/details`),
          api.get(`/bookings/${bookingId}/messages`),
        ]);
        if (!cancelled) {
          setDetails(d.data);
          setMessages(m.data);
        }
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, "Failed to load booking"));
      }
    };
    load();
    const id = setInterval(load, 6000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, bookingId]);

  // Listen for WS new_message events
  useEffect(() => {
    if (!open || !user) return;
    const ws = new WebSocket(`${WS_URL}/api/ws/${user.id}-chat-${Date.now()}`);
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "new_message" && data.booking_id === bookingId) {
          setMessages((prev) => [...prev, data.message]);
        }
      } catch {}
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [open, bookingId, user]);

  // Auto-scroll chat to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/messages`, { text: draft.trim() });
      setMessages((prev) => [...prev, data]);
      setDraft("");
    } catch (err) {
      toast.error(getErrorMessage(err, "Send failed"));
    } finally { setSending(false); }
  };

  const booking = details?.booking;
  const worker = details?.worker;

  // ETA: distance / 30 km/h average → minutes
  const dist = booking && worker ? distanceKm(
    { lat: booking.lat, lng: booking.lng },
    { lat: worker.lat, lng: worker.lng }
  ) : null;
  const etaMinutes = dist != null ? Math.max(2, Math.round((dist / 30) * 60)) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            {booking?.service_name || t("bookingDetails.title")}
            {booking && <Badge className={`${statusColor[booking.status] || ""} rounded-sm`}>{booking.status.replace(/_/g, " ")}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {!booking ? (
          <div className="py-12 text-center text-[#646964]">{t("common.loading")}</div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
            <TabsList className="bg-[#F3F1ED] rounded-sm p-1 h-10">
              <TabsTrigger value="overview" data-testid="overview-tab" className="rounded-sm data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-4 text-xs">{t("bookingDetails.tab.overview")}</TabsTrigger>
              <TabsTrigger value="track" data-testid="track-tab" className="rounded-sm data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-4 text-xs">{t("bookingDetails.tab.track")}</TabsTrigger>
              <TabsTrigger value="chat" data-testid="chat-tab" className="rounded-sm data-[state=active]:bg-[#1C4532] data-[state=active]:text-white px-4 text-xs">{t("bookingDetails.tab.chat")}</TabsTrigger>
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              {worker ? (
                <div className="bg-[#F3F1ED] p-5 rounded-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#1C4532] text-white grid place-items-center font-display text-2xl">
                      {worker.name?.[0]?.toUpperCase() || "P"}
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-xl text-[#1A1D1A]" data-testid="worker-name">{worker.name}</p>
                      <p className="text-xs text-[#646964] flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1"><Star size={12} className="fill-[#D9734E] text-[#D9734E]"/>{worker.rating?.toFixed?.(1) || "5.0"}</span>
                        <span><Hammer size={12} className="inline mr-1"/>{worker.jobs_completed || 0} jobs</span>
                        <span className={worker.is_online ? "text-[#22C55E]" : "text-[#94A3B8]"}>
                          {worker.is_online ? "● Online" : "○ Offline"}
                        </span>
                      </p>
                    </div>
                    <a href={`tel:${worker.phone || ""}`} data-testid="call-worker">
                      <Button className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white" disabled={!worker.phone}>
                        <Phone size={14} className="mr-2"/>{t("bookingDetails.call")}
                      </Button>
                    </a>
                  </div>
                  {worker.phone && <p className="mt-3 text-sm text-[#646964] font-mono">{worker.phone}</p>}
                </div>
              ) : (
                <div className="bg-[#F3F1ED] p-5 rounded-sm text-sm text-[#646964]">
                  {t("bookingDetails.noPro")}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label={t("bookingDetails.info.amount")} value={`₹${booking.amount}`}/>
                <Info label={t("bookingDetails.info.bookedAt")} value={new Date(booking.created_at).toLocaleString()}/>
                <Info label={t("bookingDetails.info.customer")} value={booking.full_name}/>
                <Info label={t("bookingDetails.info.phone")} value={booking.phone}/>
                <div className="col-span-2"><Info label={t("bookingDetails.info.address")} value={booking.address}/></div>
                {booking.notes && <div className="col-span-2"><Info label={t("bookingDetails.info.notes")} value={booking.notes}/></div>}
              </div>
            </TabsContent>

            {/* LIVE TRACK */}
            <TabsContent value="track" className="mt-4">
              {activeTab !== "track" ? null : booking.lat == null || booking.lng == null ? (
                <p className="py-8 text-center text-[#646964] text-sm">{t("bookingDetails.noLocation")}</p>
              ) : (
                <>
                  {worker && worker.lat != null && worker.lng != null ? (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <Stat icon={<MapPin size={18} strokeWidth={1.5}/>} label={t("bookingDetails.distance")} value={`${dist.toFixed(1)} km`}/>
                      <Stat icon={<Clock size={18} strokeWidth={1.5}/>} label={t("bookingDetails.eta")} value={`~${etaMinutes} min`}/>
                    </div>
                  ) : (
                    <div className="bg-[#F3F1ED] p-3 rounded-sm text-xs text-[#646964] mb-4">
                      {t("bookingDetails.proNoLocation")}
                    </div>
                  )}
                  <MapView
                    center={[booking.lat, booking.lng]}
                    userMarker={[booking.lat, booking.lng]}
                    workers={worker && worker.lat != null && worker.lng != null
                      ? [{ id: worker.id, name: worker.name, lat: worker.lat, lng: worker.lng, price: worker.price, rating: worker.rating, is_online: worker.is_online, distance_km: dist?.toFixed(1) }]
                      : []
                    }
                    height={360}
                    zoom={13}
                  />
                  {worker?.lat != null && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&origin=${booking.lat},${booking.lng}&destination=${worker.lat},${worker.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="customer-track-directions"
                      className="inline-block mt-3"
                    >
                      <Button variant="outline" className="rounded-sm border-[#1C4532] text-[#1C4532] hover:bg-[#1C4532] hover:text-white">
                        <Navigation size={14} className="mr-2"/>{t("bookingDetails.openInMaps")}
                      </Button>
                    </a>
                  )}
                </>
              )}
            </TabsContent>

            {/* CHAT */}
            <TabsContent value="chat" className="mt-4">
              {!worker ? (
                <p className="py-8 text-center text-[#646964] text-sm">{t("bookingDetails.chatLocked")}</p>
              ) : (
                <div className="border border-[#E2E0D9] rounded-sm flex flex-col h-[400px]">
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]">
                    {messages.length === 0 && <p className="text-center text-xs text-[#646964] py-8">{t("bookingDetails.noMessages")}</p>}
                    {messages.map((m) => {
                      const mine = m.sender_id === user?.id;
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] px-4 py-2 rounded-sm text-sm ${mine ? "bg-[#1C4532] text-white" : "bg-white border border-[#E2E0D9] text-[#1A1D1A]"}`}>
                            <p className="text-[10px] opacity-70 uppercase tracking-wider mb-0.5">{mine ? t("bookingDetails.you") : m.sender_name}</p>
                            <p>{m.text}</p>
                            <p className="text-[10px] opacity-50 mt-1 text-right">{new Date(m.created_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <form onSubmit={send} className="border-t border-[#E2E0D9] p-3 flex gap-2 bg-white">
                    <Input
                      data-testid="chat-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={t("bookingDetails.chatPlaceholder")}
                      className="rounded-sm border-[#E2E0D9] flex-1"
                    />
                    <Button type="submit" data-testid="chat-send" disabled={sending || !draft.trim()} className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white">
                      <Send size={14}/>
                    </Button>
                  </form>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white border border-[#E2E0D9] rounded-sm p-3">
      <p className="text-xs uppercase tracking-wider text-[#646964]">{label}</p>
      <p className="text-sm text-[#1A1D1A] mt-1">{value || "—"}</p>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-[#1C4532] text-white rounded-sm p-4 flex items-center gap-3">
      <div className="opacity-70">{icon}</div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</p>
        <p className="font-display text-xl mt-0.5">{value}</p>
      </div>
    </div>
  );
}
